import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import ProfileListFilters from '@fuww/library/src/ProfileListFilters';
import ListPage from '@fuww/library/src/Layouts/GridListPage';

import HiddenLoader from '@fuww/library/src/HiddenLoader';
import withQuery from '../lib/withQuery';
import companyProfilesQuery
from '../lib/queries/jobs/companyProfilesQuery.graphql';
import withApollo from '../lib/apollo';
import { useSiteContext } from '../components/SiteContext';
import messages from '../lib/messages.json';
import debouncedReplaceRoute from '../lib/debouncedReplaceRoute';
import replaceRoute from '../lib/replaceRoute';
import Head from '../components/Head';
import LinkAndAnchor from '../components/LinkAndAnchor';
import HomeLink from '../components/HomeLink';
import List from '../components/Company/EmployerBrandingPages/EbpList';
import {
  companyProfilesCardImageConfigurations,
  epbCardLogoConfigurations,
} from '../lib/imageConfigurations';
import DynamicWfbAds from '../components/Company/DynamicWfbAds';
import correctLocalizedRoute from '../lib/correctLocalizedRoute';

const CompanyProfilesList = withQuery(
  ({
    data,
    loading: profilesLoading,
    variables: searchVariables,
    fetchMore,
    showLoader,
  }) => {
    const { jobsLocale, jobsComplete } = useSiteContext();

    const { profilesConnection } = { ...data };
    return (
      <List
        data={data}
        listItems={profilesConnection}
        loading={profilesLoading}
        searchVariables={searchVariables}
        fetchMore={fetchMore}
        route={`jobs.${jobsLocale}`}
        useRouter={jobsComplete}
        showLoader={showLoader}
        loader={<HiddenLoader />}
      />
    );
  },
  { renderAlways: true },
);

const ITEM_LIMIT = 8;

export const CompanyProfiles = () => {
  const intl = useIntl();
  const {
    query: {
      section,
      search: searchParameter,
    },
  } = useRouter();
  const { query: queryParameters } = useRouter();

  const [search, setSearch] = useState(searchParameter || '');

  const { jobsLocale, jobsComplete } = useSiteContext();
  const route = `company-profiles.${jobsLocale}`;

  const breadcrumbs = [
    <HomeLink />,
    <LinkAndAnchor
      useRouter={jobsComplete}
      route={`jobs.${jobsLocale}`}
    >
      {intl.formatMessage(messages.jobs)}
    </LinkAndAnchor>,
    <LinkAndAnchor
      useRouter={jobsComplete}
      route={route}
    >
      {intl.formatMessage(messages.workAt)}
    </LinkAndAnchor>,
  ];

  return (
    <>
      <Head
        title={intl.formatMessage(messages['company-profiles.title'])}
        description={intl
          .formatMessage(messages['company-profiles.description'])}
      />
      <ListPage
        breadcrumbs={breadcrumbs}
        title={intl.formatMessage(messages['company-profiles.title'])}
        topSlot={<DynamicWfbAds />}
        filters={(
          <ProfileListFilters
            searchId="company-profile-directory-search"
            searchValue={search}
            searchLabel={intl.formatMessage(messages.searchTitle)}
            searchOnChange={({ target: { value } }) => {
              const { search: _search, ...rest } = queryParameters;
              debouncedReplaceRoute(
                route, { ...rest, ...(value ? { search: value } : {}) },
              );
              setSearch(value);
            }}
            searchOnClear={() => {
              setSearch('');
              const { search: _search, ...rest } = queryParameters;
              replaceRoute(route, {
                ...rest,
              });
            }}
            premiumOnChange={({ target: { checked } }) => {
              const { section: _section, ...rest } = queryParameters;
              replaceRoute(
                route, { ...rest, ...(checked ? {} : { section: 'all' }) },
              );
            }}
            premiumChecked={section !== 'all'}
            maxWidth={900}
          />
        )}
        list={(
          <CompanyProfilesList
            query={companyProfilesQuery}
            errorMessage="Could not load jobs by company"
            variables={{
              locales: [jobsLocale],
              first: ITEM_LIMIT,
              imageConfigurations: companyProfilesCardImageConfigurations,
              logoConfigurations: epbCardLogoConfigurations,
              search,
              premium: section !== 'all' || null,
            }}
            loader={<HiddenLoader />}
          />
        )}
      />
    </>
  );
};

CompanyProfiles.getInitialProps = (context) => {
  correctLocalizedRoute(context, 'company-profiles');

  return {};
};

export default withApollo(CompanyProfiles);
